/**
 * The editor inside PRISM: connect the bridge (PRISM origins from /api/v1/config), wait for host:init, then open
 * what PRISM asked for: mode 'new' → the template picker (a template or a blank draft on PRISM's default canvas);
 * mode 'edit' → POST /api/v1/prism/open (the scene restored from the package, the user's existing draft of that
 * version, or the read-only view; with unsaved changes in that draft the user chooses: continue it or start fresh).
 * The token stays in memory (prism/session.ts).
 *
 * A new graphic starts in PRISM's default format (host:init defaults; PRISM 1.0 and 1.1 always send 1920×1080 at
 * 25, whatever the sequence): the format select above the templates preselects it and filters the templates to its
 * shape, and the user can pick another format (9:16, 4:5, 1:1) or every shape (ADR 0036). Further layouts (ADR 0037)
 * are preselected from host:init defaults.layouts (bridge 1.1.0; e.g. a multi-format sequence's other formats); a
 * template or blank draft gets them.
 */
import { useEffect, useState } from 'react';
import { aspectOf, rateFor, ratesFor, sameAspect, type FrameRate } from '@prism-graphics/scene-schema';
import { ApiError, api, type ClientConfig, type PrismOpened } from '../api.ts';
import { TemplatePicker } from '../TemplatePicker.tsx';
import { EditorPage } from '../EditorPage.tsx';
import { t } from '../i18n.ts';
import { FormatOptions, LayoutChoice, RateOptions, isPreset, parseRate, parseSize, rateValue, sizeKey } from '../ui/formats.tsx';
import { ReadOnlyView } from './ReadOnlyView.tsx';
import { prism, usePrism } from './session.ts';

type Target =
  | { kind: 'draft'; draftId: string }
  | { kind: 'readonly'; opened: PrismOpened }
  | { kind: 'error'; message: string }
  | { kind: 'pick' }
  | { kind: 'resume'; opened: PrismOpened };

/** One open per host:init, also under StrictMode's double effects. */
let opening: { init: unknown; promise: Promise<Target> } | undefined;

function failed(e: unknown): Target {
  const err = e instanceof ApiError ? e : undefined;
  const code = err?.status === 403 ? 'PERMISSION_DENIED' : err?.status === 401 ? 'TOKEN_EXPIRED' : 'INIT_INVALID';
  if (err?.status === 401) prism.expire();
  prism.bridge?.error({ code, message: e instanceof Error ? e.message : String(e) });
  return { kind: 'error', message: err?.status === 403 ? t('embedded.forbidden', { message: err.message }) : e instanceof Error ? e.message : String(e) };
}

async function open(resume?: 'continue' | 'fresh'): Promise<Target> {
  const init = prism.state.init!;
  try {
    const opened = await api.openFromPrism(init.assetId!, undefined, resume);
    if (opened.resumable) return { kind: 'resume', opened };
    return opened.readOnly || !opened.draftId ? { kind: 'readonly', opened } : { kind: 'draft', draftId: opened.draftId };
  } catch (e) {
    return failed(e);
  }
}

type Canvas = { width: number; height: number; frameRate: FrameRate };

/** A blank draft on `canvas`, or a template's copy (at canvas.frameRate when the format allows it), with `layouts`. */
async function create(body: { template?: string; brand?: boolean; canvas: Canvas; layouts?: Array<{ width: number; height: number }> }): Promise<Target> {
  try {
    const draft = await api.createDraft(body);
    return { kind: 'draft', draftId: draft.id };
  } catch (e) {
    return failed(e);
  }
}

async function openTarget(): Promise<Target> {
  const init = prism.state.init!;
  try {
    if (init.mode === 'edit' && init.assetId) return await open();
    return { kind: 'pick' };
  } catch (e) {
    return failed(e);
  }
}

export function EmbeddedApp({ config, configError }: { config: ClientConfig | undefined; configError: string | undefined }) {
  const status = usePrism((s) => s.status);
  const init = usePrism((s) => s.init);
  const [target, setTarget] = useState<Target>();

  useEffect(() => {
    if (config) prism.start(config);
  }, [config]);

  useEffect(() => {
    if (status !== 'ready' || !init || target) return;
    if (!opening || opening.init !== init) opening = { init, promise: openTarget() };
    let alive = true;
    void opening.promise.then((t2) => alive && setTarget(t2));
    return () => {
      alive = false;
    };
  }, [status, init, target]);

  if (configError) return <div className="center-message" role="alert">{configError}</div>;
  if (!target) {
    return (
      <div className="center-message" data-testid="embedded-waiting" data-status={status}>
        <p>{status === 'expired' ? t('embedded.expired') : status === 'ready' ? t('embedded.opening') : t('embedded.waiting')}</p>
      </div>
    );
  }
  if (target.kind === 'error') {
    return (
      <div className="center-message" role="alert" data-testid="embedded-error">
        <p>{target.message}</p>
        <button type="button" className="btn" onClick={() => prism.bridge?.close({ saved: false })}>
          {t('embedded.close')}
        </button>
      </div>
    );
  }
  if (target.kind === 'readonly') return <ReadOnlyView opened={target.opened} onClose={() => prism.bridge?.close({ saved: false })} />;
  if (target.kind === 'pick') return <PickTemplate onTarget={setTarget} />;
  if (target.kind === 'resume') return <ResumePrompt opened={target.opened} onTarget={setTarget} />;
  return <EditorPage key={target.draftId} draftId={target.draftId} config={config} onHome={() => undefined} embedded />;
}

function PickTemplate({ onTarget }: { onTarget: (t: Target) => void }) {
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(false);
  const d = prism.state.init?.defaults;
  const start = { width: d?.width ?? 1920, height: d?.height ?? 1080 };
  const [size, setSize] = useState(start);
  const [rate, setRate] = useState<FrameRate>(() => (d ? { ...d.frameRate } : { num: 25, den: 1 }));
  const [layouts, setLayouts] = useState(() => (d?.layouts ?? []).filter((l) => !sameAspect(l, start)).map((l) => ({ width: l.width, height: l.height })));
  useEffect(() => {
    api.brandKit().then((b) => setBrand(b.revision > 0), () => undefined);
  }, []);
  const go = (body: { template?: string; brand?: boolean; canvas: Canvas }) => {
    setBusy(true);
    void create({ ...body, ...(layouts.length ? { layouts } : {}) }).then(onTarget);
  };
  const rates = ratesFor(size);
  return (
    <div className="start embedded-pick" data-testid="embedded-new">
      <header className="start-head">
        <h1>{t('templates.pickTitle')}</h1>
        <p className="muted">{t('templates.pickHint')}</p>
      </header>
      <div className="row two embedded-format">
        <label className="field select">
          <span className="field-label">{t('top.resolution')}</span>
          <select
            value={isPreset(size) ? sizeKey(size) : 'custom'}
            data-testid="embedded-format"
            onChange={(e) => {
              const next = e.target.value === 'custom' ? start : parseSize(e.target.value);
              setSize(next);
              setRate(rateFor(next, rate));
              setLayouts(layouts.filter((l) => !sameAspect(l, next)));
            }}
          >
            <FormatOptions current={start} />
          </select>
        </label>
        <label className="field select">
          <span className="field-label">{t('top.frameRate')}</span>
          <select value={rateValue(rate, rates)} data-testid="embedded-rate" onChange={(e) => setRate(parseRate(e.target.value))}>
            <RateOptions rates={rates} current={rate} />
          </select>
        </label>
      </div>
      <LayoutChoice primary={size} value={layouts} onChange={setLayouts} testId="embedded-layouts" />
      <TemplatePicker
        aspect={aspectOf(size)}
        busy={busy}
        brandAvailable={brand}
        onBlank={() => go({ canvas: { ...size, frameRate: rate } })}
        onPick={(template, b) => go({ template, brand: b, canvas: { ...size, frameRate: rate } })}
      />
    </div>
  );
}

function ResumePrompt({ opened, onTarget }: { opened: PrismOpened; onTarget: (t: Target) => void }) {
  const [busy, setBusy] = useState(false);
  const choose = (resume: 'continue' | 'fresh') => {
    setBusy(true);
    void open(resume).then(onTarget);
  };
  return (
    <div className="center-message" role="alertdialog" aria-labelledby="resume-h" data-testid="resume-prompt">
      <h2 id="resume-h">{t('embedded.resumeTitle')}</h2>
      <p>{t('embedded.resumeBody', { version: opened.version, when: new Date(opened.resumable!.updatedAt).toLocaleString() })}</p>
      <div className="row-buttons">
        <button type="button" className="btn primary" disabled={busy} onClick={() => choose('continue')} data-testid="resume-continue">
          {t('embedded.resumeContinue')}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => choose('fresh')} data-testid="resume-fresh">
          {t('embedded.resumeFresh')}
        </button>
      </div>
    </div>
  );
}
